// ============================================
// Login Page - OTP Authentication (Bypass Mode)
// ============================================

import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Phone, Shield, ArrowRight, Loader2 } from 'lucide-react';
import { useAuthStore, useToast } from '../stores';
import { cn } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';

export function Login() {
  const toast = useToast();
  const { t, isRTL } = useTranslation();
  const { isAuthenticated, isLoading, startLogin, verifyOtp, error, clearError } = useAuthStore();

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error]);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone || phone.length < 9) {
      toast.error(t('auth.invalidPhone'));
      return;
    }
    
    const formattedPhone = phone.startsWith('+') ? phone : `+966${phone.replace(/^0/, '')}`;
    const result = await startLogin(formattedPhone);
    
    if (result.ok) {
      if (result.bypass) {
        toast.success(t('auth.loginSuccess'));
      } else {
        setStep('otp');
        toast.info(t('auth.otpSent'));
      }
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error(t('auth.invalidOtp'));
      return;
    }

    const formattedPhone = phone.startsWith('+') ? phone : `+966${phone.replace(/^0/, '')}`;
    const result = await verifyOtp(formattedPhone, otp);
    
    if (result.ok) {
      toast.success(t('auth.loginSuccess'));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">{t('app.title')}</h1>
          <p className="text-slate-400 mt-2">{t('app.subtitle')}</p>
        </div>

        {/* Login Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 shadow-xl">
          {step === 'phone' ? (
            <form onSubmit={handlePhoneSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t('auth.phoneNumber')}
                </label>
                <div className="relative">
                  <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="05XXXXXXXX"
                    className="input w-full pr-10 text-left ltr"
                    dir="ltr"
                    autoComplete="tel"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">{t('auth.phoneHint')}</p>
              </div>

              <button
                type="submit"
                disabled={isLoading || !phone}
                className="btn-primary w-full justify-center"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t('auth.continue')}
                    <ArrowRight className={cn('w-5 h-5', isRTL && 'rtl-flip')} />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-5">
              <div className="text-center mb-4">
                <p className="text-slate-300">{t('auth.otpSentTo')}</p>
                <p className="text-white font-mono text-lg mt-1" dir="ltr">{phone}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t('auth.verificationCode')}
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="input w-full text-center text-2xl tracking-widest font-mono"
                  dir="ltr"
                  autoComplete="one-time-code"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || otp.length !== 6}
                className="btn-primary w-full justify-center"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t('auth.verify')}
                    <Shield className="w-5 h-5" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => { setStep('phone'); setOtp(''); }}
                className="w-full text-sm text-slate-400 hover:text-white transition-colors"
              >
                {t('auth.changePhone')}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          {t('auth.securedBy')}
        </p>
      </div>
    </div>
  );
}
